import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  FileText,
  FileSpreadsheet,
  Share2,
  RotateCcw,
  Filter,
  Calendar,
  Building2,
  Users,
  MapPin,
  Coins,
  IndianRupee,
  Truck,
  HardHat,
  Wallet,
  Boxes,
  Package,
  Repeat,
  ChevronDown,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useProject, useLedger } from '../lib/project';
import {
  useProjectPickerList,
  useAllProjectsReportData,
  paymentsSection,
  materialSummarySection,
  workerSummarySection,
  supplierSummarySection,
  subcontractorSummarySection,
  expenseSummarySection,
  transferSection,
  stockSummarySection,
  financialSummarySection,
  allProjectsOverviewSection,
  allProjectsPaymentsSection,
  allProjectsMaterialsSection,
  allProjectsUsageSection,
  allProjectsLabourSection,
  allProjectsSubcontractorsSection,
  allProjectsSuppliersSection,
  allProjectsExpensesSection,
  allProjectsTransfersSection,
  allProjectsFinancialSection,
  type ReportSection,
} from '../lib/project-report';
import {
  exportExcelSections,
  exportPdfSections,
  sharePdfSections,
  canShare,
  exportExcelSection,
  exportPdfSection,
  sharePdfSection,
  type ExportSection,
  type ExportMeta,
} from '../lib/export';
import Shell from '../components/Shell';
import {
  StatusChip,
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  useToast,
  Select,
} from '../components/ui';
import MaterialReportDrawer from '../components/MaterialReportDrawer';

export default function ProjectReport() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: picker, isLoading: pickerLoading } = useProjectPickerList();

  const [clientId, setClientId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [projectId, setProjectId] = useState('');
  const [ran, setRan] = useState(false);
  const [busy, setBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<{ id?: string; name?: string } | null>(null);

  const clients = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of picker ?? []) if (!seen.has(p.clientId)) seen.set(p.clientId, p.clientName);
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [picker]);

  const availableProjects = useMemo(
    () => (picker ?? []).filter((p) => !clientId || p.clientId === clientId),
    [picker, clientId],
  );

  const filtered = !!clientId || !!fromDate || !!toDate || !!projectId;
  const resetFilters = () => {
    setClientId('');
    setFromDate('');
    setToDate('');
    setProjectId('');
    setRan(false);
  };

  const narrow = (fn: (v: string) => void) => (v: string) => {
    fn(v);
    setRan(false);
  };

  const isSingleProject = ran && !!projectId;
  const isAllProjects = ran && !projectId;

  // Single project queries
  const activeId = isSingleProject ? projectId : '';
  const project = useProject(activeId);
  const payments = useLedger(activeId, 'payments');
  const purchases = useLedger(activeId, 'purchases');
  const usage = useLedger(activeId, 'usage');
  const labour = useLedger(activeId, 'labour');
  const expenses = useLedger(activeId, 'expenses');
  const transfers = useLedger(activeId, 'transfers');

  // All projects query
  const allProjectsQuery = useAllProjectsReportData(isAllProjects);

  const singleQueries = [project, payments, purchases, usage, labour, expenses, transfers];
  const loading = ran && (isSingleProject ? singleQueries.some((q) => q.isLoading) : allProjectsQuery.isLoading);
  const anyError = isSingleProject
    ? singleQueries.find((q) => q.error)?.error ?? null
    : allProjectsQuery.error instanceof Error
    ? allProjectsQuery.error.message
    : allProjectsQuery.error
    ? String(allProjectsQuery.error)
    : null;

  type AnyRow = Record<string, unknown>;
  function applyDateAndClientFilter(dateKey: string, rows: AnyRow[], projectList?: Array<{ id: string }>): AnyRow[] {
    const allowedProjectIds = projectList ? new Set(projectList.map((p) => p.id)) : null;
    return rows.filter((r) => {
      const d = String(r[dateKey] ?? '');
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      if (allowedProjectIds && r.project_id && !allowedProjectIds.has(String(r.project_id))) return false;
      return true;
    });
  }

  // Filtered rows for Single Project
  const singleFilteredPayments = useMemo(
    () => applyDateAndClientFilter('paid_on', payments.data ?? []),
    [payments.data, fromDate, toDate],
  );
  const singleFilteredPurchases = useMemo(
    () => applyDateAndClientFilter('purchased_on', purchases.data ?? []),
    [purchases.data, fromDate, toDate],
  );
  const singleFilteredUsage = useMemo(
    () => applyDateAndClientFilter('used_on', usage.data ?? []),
    [usage.data, fromDate, toDate],
  );
  const singleFilteredLabour = useMemo(
    () => applyDateAndClientFilter('worked_on', labour.data ?? []),
    [labour.data, fromDate, toDate],
  );
  const singleFilteredExpenses = useMemo(
    () => applyDateAndClientFilter('spent_on', expenses.data ?? []),
    [expenses.data, fromDate, toDate],
  );
  const singleFilteredTransfers = useMemo(
    () => applyDateAndClientFilter('transferred_on', transfers.data ?? []),
    [transfers.data, fromDate, toDate],
  );

  // Filtered rows for All Projects
  const allData = allProjectsQuery.data;
  const filteredAllProjectList = useMemo(() => {
    if (!allData?.projects) return [];
    return clientId ? allData.projects.filter((p) => p.client_id === clientId) : allData.projects;
  }, [allData?.projects, clientId]);

  const allFilteredPayments = useMemo(
    () => applyDateAndClientFilter('paid_on', allData?.payments ?? [], filteredAllProjectList),
    [allData?.payments, fromDate, toDate, filteredAllProjectList],
  );
  const allFilteredPurchases = useMemo(
    () => applyDateAndClientFilter('purchased_on', allData?.purchases ?? [], filteredAllProjectList),
    [allData?.purchases, fromDate, toDate, filteredAllProjectList],
  );
  const allFilteredUsage = useMemo(
    () => applyDateAndClientFilter('used_on', allData?.usage ?? [], filteredAllProjectList),
    [allData?.usage, fromDate, toDate, filteredAllProjectList],
  );
  const allFilteredLabour = useMemo(
    () => applyDateAndClientFilter('worked_on', allData?.labour ?? [], filteredAllProjectList),
    [allData?.labour, fromDate, toDate, filteredAllProjectList],
  );
  const allFilteredExpenses = useMemo(
    () => applyDateAndClientFilter('spent_on', allData?.expenses ?? [], filteredAllProjectList),
    [allData?.expenses, fromDate, toDate, filteredAllProjectList],
  );
  const allFilteredTransfers = useMemo(
    () => applyDateAndClientFilter('transferred_on', allData?.transfers ?? [], filteredAllProjectList),
    [allData?.transfers, fromDate, toDate, filteredAllProjectList],
  );

  const sections: ReportSection[] = useMemo(() => {
    if (!ran) return [];

    if (isSingleProject && project.data) {
      return [
        paymentsSection(singleFilteredPayments),
        materialSummarySection(singleFilteredPurchases),
        workerSummarySection(singleFilteredLabour),
        supplierSummarySection(singleFilteredPurchases),
        subcontractorSummarySection(singleFilteredLabour),
        expenseSummarySection(singleFilteredExpenses),
        transferSection(singleFilteredTransfers, activeId),
        stockSummarySection(singleFilteredPurchases, singleFilteredUsage),
        financialSummarySection(project.data, singleFilteredPurchases, singleFilteredLabour, singleFilteredExpenses),
      ];
    }

    if (isAllProjects && allData) {
      return [
        allProjectsOverviewSection(filteredAllProjectList, allFilteredPayments, allFilteredPurchases, allFilteredLabour, allFilteredExpenses),
        allProjectsPaymentsSection(allFilteredPayments),
        allProjectsMaterialsSection(allFilteredPurchases),
        allProjectsUsageSection(allFilteredUsage),
        allProjectsLabourSection(allFilteredLabour),
        allProjectsSubcontractorsSection(allFilteredLabour),
        allProjectsSuppliersSection(allFilteredPurchases),
        allProjectsExpensesSection(allFilteredExpenses),
        allProjectsTransfersSection(allFilteredTransfers),
        allProjectsFinancialSection(filteredAllProjectList, allFilteredPayments, allFilteredPurchases, allFilteredLabour, allFilteredExpenses),
      ];
    }

    return [];
  }, [ran, isSingleProject, isAllProjects, project.data, allData, filteredAllProjectList,
      singleFilteredPayments, singleFilteredPurchases, singleFilteredUsage,
      singleFilteredLabour, singleFilteredExpenses, singleFilteredTransfers, activeId,
      allFilteredPayments, allFilteredPurchases, allFilteredUsage, allFilteredLabour,
      allFilteredExpenses, allFilteredTransfers]);

  function buildMeta(): ExportMeta {
    const filters: string[] = [];
    const clientName = clients.find((c) => c.id === clientId)?.name;
    if (clientId && clientName) filters.push(`Client: ${clientName}`);
    if (projectId) {
      const projName = picker?.find((p) => p.id === projectId)?.name;
      if (projName) filters.push(`Project: ${projName}`);
    } else {
      filters.push('Project: All Projects');
    }
    if (fromDate) filters.push(`From: ${fromDate}`);
    if (toDate) filters.push(`To: ${toDate}`);

    if (isSingleProject && project.data) {
      return {
        title: `Project Report \u2014 ${project.data.name}`,
        subtitle: `${project.data.clientName ?? ''}${project.data.location ? ` \u00b7 ${project.data.location}` : ''}`,
        filters,
      };
    }

    return {
      title: clientName ? `Projects Report \u2014 ${clientName}` : 'All Projects Consolidated Report',
      subtitle: `${filteredAllProjectList.length} Projects \u00b7 Multi-Project Consolidated Overview`,
      filters,
    };
  }

  async function runExport(kind: 'excel' | 'pdf') {
    if (!sections.length) return;
    setBusy(kind);
    try {
      const meta = buildMeta();
      if (kind === 'excel') {
        await exportExcelSections(sections, meta);
        toast.show('Excel report downloaded');
      } else {
        await exportPdfSections(sections, meta);
        toast.show('PDF report downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not export file', true);
    } finally {
      setBusy(null);
    }
  }

  async function runShare() {
    if (!sections.length) return;
    setBusy('share');
    try {
      const meta = buildMeta();
      const shared = await sharePdfSections(sections, meta);
      if (!shared) toast.show('Sharing is not supported on this device', true);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not share file', true);
    } finally {
      setBusy(null);
    }
  }

  const ready = ran && !loading && !anyError && ((isSingleProject && !!project.data) || (isAllProjects && !!allData));
  const sharing = canShare();

  // Multi-project KPI calculations
  const allTotalBudget = filteredAllProjectList.reduce((s, p) => s + Number(p.value_paise ?? 0), 0);
  const allTotalReceived = allFilteredPayments.reduce((s, p) => s + Number(p.amount_paise ?? 0), 0);
  const allTotalMaterialCost = allFilteredPurchases.reduce((s, p) => s + Number(p.amount_paise ?? 0), 0);
  const allTotalLabourCost = allFilteredLabour.reduce((s, p) => s + Number(p.total_amount_paise ?? 0), 0);
  const allTotalExpenseCost = allFilteredExpenses.reduce((s, p) => s + Number(p.amount_paise ?? 0), 0);
  const allTotalSpent = allTotalMaterialCost + allTotalLabourCost + allTotalExpenseCost;
  const allTotalNet = allTotalReceived - allTotalSpent;
  const allTotalPending = Math.max(0, allTotalBudget - allTotalReceived);

  return (
    <Shell title="Project Financial & Operations Report" crumb="Reports">
      <button
        className="btn btn-auto btn-sm btn-ghost"
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-soft)',
          paddingLeft: 4,
        }}
        onClick={() => navigate('/reports')}
      >
        <ArrowLeft size={14} />
        <span>Back to Reports Directory</span>
      </button>

      {/* Query Filter Matrix */}
      <div className="card" style={{ marginBottom: 20, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Filter size={15} style={{ color: 'var(--brand)' }} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
            Project Report Parameters
          </h3>
        </div>

        <div
          className="form-grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
        >
          <div className="field">
            <label>Client</label>
            <Select
              value={clientId}
              onChange={(val) => narrow(setClientId)(val)}
              disabled={pickerLoading}
              placeholder="All Clients"
              options={[
                { value: '', label: 'All Clients' },
                ...clients.map((c) => ({
                  value: c.id,
                  label: c.name,
                })),
              ]}
            />
          </div>

          <div className="field">
            <label>Project</label>
            <Select
              value={projectId}
              onChange={(val) => narrow(setProjectId)(val)}
              disabled={pickerLoading}
              placeholder="All Projects (Consolidated Report)"
              options={[
                { value: '', label: 'All Projects (Consolidated Report)' },
                ...availableProjects.map((p) => ({
                  value: p.id,
                  label: p.name,
                })),
              ]}
            />
          </div>

          <div className="field">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setRan(false);
              }}
            />
          </div>

          <div className="field">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setRan(false);
              }}
            />
          </div>
        </div>

        <div className="report-actions-bar">
          <div className="report-actions-primary">
            <button
              className="btn btn-primary btn-auto btn-sm"
              onClick={() => setRan(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileText size={14} />
              <span>Generate Report</span>
            </button>

            <button
              className="btn btn-auto btn-sm btn-ghost"
              onClick={resetFilters}
              disabled={!filtered}
              style={{ display: 'flex', alignItems: 'center', gap: 5, borderRadius: 'var(--radius-sm)' }}
            >
              <RotateCcw size={13} />
              <span>Reset Filters</span>
            </button>
          </div>

          <div className="report-actions-exports">
            <button
              className="btn btn-auto btn-sm"
              disabled={!ready || !!busy}
              onClick={() => void runExport('excel')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileSpreadsheet size={13} />
              <span>{busy === 'excel' ? 'Exporting…' : 'Export Excel'}</span>
            </button>

            <button
              className="btn btn-auto btn-sm"
              disabled={!ready || !!busy}
              onClick={() => void runExport('pdf')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileText size={13} />
              <span>{busy === 'pdf' ? 'Exporting…' : 'Export PDF'}</span>
            </button>

            <button
              className="btn btn-auto btn-sm"
              disabled={!ready || !!busy}
              onClick={() => void runShare()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Share2 size={13} />
              <span>{busy === 'share' ? 'Sharing…' : 'Share'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {!ran ? (
        <div className="card" style={{ padding: '40px 20px' }}>
          <EmptyState
            icon="briefcase"
            title="Click Generate Report to inspect project financial ledgers"
            hint="Select All Projects for portfolio summaries or choose an individual site to inspect 9 granular ledgers."
          />
        </div>
      ) : loading ? (
        <div className="card">
          <TableSkeleton rows={5} cols={4} />
        </div>
      ) : anyError ? (
        <div className="card">
          <ErrorState
            error={anyError}
            onRetry={() => {
              if (isSingleProject) {
                void project.refetch();
                void payments.refetch();
                void purchases.refetch();
                void usage.refetch();
                void labour.refetch();
                void expenses.refetch();
                void transfers.refetch();
              } else {
                void allProjectsQuery.refetch();
              }
            }}
          />
        </div>
      ) : isSingleProject && !project.data ? (
        <div className="card">
          <EmptyState icon="briefcase" title="Project not found" />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Single Project Identity Header */}
          {isSingleProject && project.data && (
            <>
              <div
                className="card"
                style={{
                  padding: '20px 24px',
                  marginBottom: 20,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                    {project.data.name}
                  </h2>
                  <div
                    style={{
                      display: 'flex',
                      gap: 16,
                      flexWrap: 'wrap',
                      marginTop: 8,
                      fontSize: 13,
                      color: 'var(--text-soft)',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Users size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{project.data.clientName}</span>
                    </span>

                    {project.data.location && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <MapPin size={13} style={{ color: 'var(--text-faint)' }} />
                        <span>{project.data.location}</span>
                      </span>
                    )}

                    {project.data.startDate && (
                      <span>Started {formatDate(project.data.startDate)}</span>
                    )}

                    <StatusChip value={project.data.status} />

                    {(fromDate || toDate) && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          background: 'rgba(37, 99, 235, 0.08)',
                          color: 'var(--brand)',
                          padding: '2px 9px',
                          borderRadius: 'var(--radius-xs)',
                          fontSize: 12,
                          fontWeight: 600,
                          border: '1px solid rgba(37, 99, 235, 0.2)',
                        }}
                      >
                        <Calendar size={12} />
                        {fromDate && toDate
                          ? `${fromDate} – ${toDate}`
                          : fromDate
                          ? `From ${fromDate}`
                          : `Up to ${toDate}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Single Project KPIs */}
              {(() => {
                const extraPaise = Math.max(0, (project.data.paidPaise || 0) - (project.data.valuePaise || 0));
                return (
                  <div className="kpi-row" style={{ marginBottom: 20 }}>
                    <div className="kpi">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span className="lbl">Total Project Budget</span>
                        <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                          <IndianRupee size={14} />
                        </div>
                      </div>
                      <div className="val num" style={{ fontSize: 20 }}>
                        {formatMoney(project.data.valuePaise)}
                      </div>
                    </div>

                    <div className="kpi">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span className="lbl">Total Received</span>
                        <div style={{ color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                          <CheckCircle2 size={14} />
                        </div>
                      </div>
                      <div className="val num" style={{ fontSize: 20, color: '#16a34a' }}>
                        {formatMoney(project.data.paidPaise)}
                      </div>
                      {extraPaise > 0 && (
                        <div className="sub" style={{ marginTop: 4, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                          +{formatMoney(extraPaise)} surplus over budget
                        </div>
                      )}
                    </div>

                    <div className="kpi">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span className="lbl">Pending Collections</span>
                        <div
                          style={{
                            color: project.data.pendingPaise > 0 ? '#ea580c' : (extraPaise > 0 ? '#16a34a' : 'var(--text-faint)'),
                            background: project.data.pendingPaise > 0 ? 'rgba(234, 88, 12, 0.1)' : (extraPaise > 0 ? 'rgba(22, 163, 74, 0.1)' : 'var(--surface-muted)'),
                            padding: 5,
                            borderRadius: 'var(--radius-xs)',
                          }}
                        >
                          <Coins size={14} />
                        </div>
                      </div>
                      <div
                        className="val num"
                        style={{
                          fontSize: 20,
                          color: project.data.pendingPaise > 0 ? '#ea580c' : (extraPaise > 0 ? '#16a34a' : 'var(--text-soft)'),
                        }}
                      >
                        {formatMoney(project.data.pendingPaise)}
                      </div>
                      <div className="sub" style={{ marginTop: 4, fontSize: 12 }}>
                        {project.data.pendingPaise > 0
                          ? 'collection pending'
                          : extraPaise > 0
                            ? `fully settled (+${formatMoney(extraPaise)} surplus)`
                            : 'fully settled account'}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* All Projects Consolidated Header */}
          {isAllProjects && allData && (
            <>
              <div
                className="card"
                style={{
                  padding: '20px 24px',
                  marginBottom: 20,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                    {clientId
                      ? `Projects Report — ${clients.find((c) => c.id === clientId)?.name ?? 'Client'}`
                      : 'All Projects Consolidated Portfolio Report'}
                  </h2>
                  <div
                    style={{
                      display: 'flex',
                      gap: 16,
                      flexWrap: 'wrap',
                      marginTop: 8,
                      fontSize: 13,
                      color: 'var(--text-soft)',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Building2 size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{filteredAllProjectList.length} Active Sites</span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Wallet size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{allFilteredPayments.length} Client Receipts</span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Truck size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{allFilteredPurchases.length} Material Purchases</span>
                    </span>
                    {(fromDate || toDate) && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          background: 'rgba(37, 99, 235, 0.08)',
                          color: 'var(--brand)',
                          padding: '2px 9px',
                          borderRadius: 'var(--radius-xs)',
                          fontSize: 12,
                          fontWeight: 600,
                          border: '1px solid rgba(37, 99, 235, 0.2)',
                        }}
                      >
                        <Calendar size={12} />
                        {fromDate && toDate
                          ? `${fromDate} – ${toDate}`
                          : fromDate
                          ? `From ${fromDate}`
                          : `Up to ${toDate}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Portfolio Multi-KPI */}
              <div
                className="kpi-row portfolio-kpis"
                style={{
                  marginBottom: 20,
                }}
              >
                <div className="kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span className="lbl">Portfolio Budget</span>
                    <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                      <IndianRupee size={14} />
                    </div>
                  </div>
                  <div className="val num" style={{ fontSize: 20 }}>{formatMoney(allTotalBudget)}</div>
                </div>

                <div className="kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span className="lbl">Collections Received</span>
                    <div style={{ color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                      <CheckCircle2 size={14} />
                    </div>
                  </div>
                  <div className="val num" style={{ fontSize: 20, color: '#16a34a' }}>
                    {formatMoney(allTotalReceived)}
                  </div>
                </div>

                <div className="kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span className="lbl">Total Expenditure</span>
                    <div style={{ color: '#ea580c', background: 'rgba(234, 88, 12, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                      <Wallet size={14} />
                    </div>
                  </div>
                  <div className="val num" style={{ fontSize: 20, color: '#ea580c' }}>
                    {formatMoney(allTotalSpent)}
                  </div>
                </div>

                <div className="kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span className="lbl">Net Cash Position</span>
                    <div style={{ color: allTotalNet >= 0 ? '#16a34a' : '#dc2626', background: allTotalNet >= 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                      <Coins size={14} />
                    </div>
                  </div>
                  <div
                    className="val num"
                    style={{ fontSize: 20, color: allTotalNet >= 0 ? '#16a34a' : '#dc2626' }}
                  >
                    {formatMoney(allTotalNet)}
                  </div>
                </div>

                <div className="kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span className="lbl">Pending Collections</span>
                    <div
                      style={{
                        color: allTotalPending > 0 ? '#ea580c' : 'var(--text-faint)',
                        background: allTotalPending > 0 ? 'rgba(234, 88, 12, 0.1)' : 'var(--surface-muted)',
                        padding: 5,
                        borderRadius: 'var(--radius-xs)',
                      }}
                    >
                      <AlertCircle size={14} />
                    </div>
                  </div>
                  <div
                    className="val num"
                    style={{ fontSize: 20, color: allTotalPending > 0 ? '#ea580c' : 'var(--text-soft)' }}
                  >
                    {formatMoney(allTotalPending)}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Accordion Sections with Exports */}
          <div className="accordion">
            {sections.map((s) => (
              <SectionAccordion
                key={s.key}
                section={s}
                meta={buildMeta()}
                sharing={sharing}
                onSelectMaterial={(id, name) => setSelectedMaterial({ id, name })}
              />
            ))}
          </div>

          {/* Material drill-down drawer */}
          {selectedMaterial && (
            <MaterialReportDrawer
              materialId={selectedMaterial.id}
              materialName={selectedMaterial.name}
              onClose={() => setSelectedMaterial(null)}
            />
          )}
        </motion.div>
      )}
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
/* Section Accordion Component with Center-Aligned Table               */
/* ------------------------------------------------------------------ */

function SectionAccordion({
  section,
  meta,
  sharing,
  onSelectMaterial,
}: {
  section: ReportSection;
  meta: ExportMeta;
  sharing: boolean;
  onSelectMaterial?: (id?: string, name?: string) => void;
}) {
  const toast = useToast();
  const [secBusy, setSecBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);
  const hasRows = section.rows.length > 0;

  async function doSectionExport(kind: 'excel' | 'pdf') {
    setSecBusy(kind);
    try {
      if (kind === 'excel') {
        await exportExcelSection(section as ExportSection, meta);
        toast.show('Excel downloaded');
      } else {
        await exportPdfSection(section as ExportSection, meta);
        toast.show('PDF downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not create the file', true);
    } finally {
      setSecBusy(null);
    }
  }

  async function doSectionShare() {
    setSecBusy('share');
    try {
      const shared = await sharePdfSection(section as ExportSection, meta);
      if (!shared) {
        await exportPdfSection(section as ExportSection, meta);
        toast.show('PDF downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not share', true);
    } finally {
      setSecBusy(null);
    }
  }

  return (
    <details className="accordion-item" open>
      <summary className="accordion-summary">
        <div className="accordion-icon-box">
          <Building2 size={18} className="accordion-icon" />
        </div>
        <span className="accordion-title">{section.title}</span>
        <span className="count-badge">
          {section.rows.length} record{section.rows.length === 1 ? '' : 's'}
        </span>

        {/* Action buttons inside accordion summary (Desktop / Tablet view) */}
        <div
          className="section-actions desktop-only"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="btn btn-auto btn-sm btn-ghost"
            disabled={!hasRows || !!secBusy}
            title={`Download ${section.title} as Excel`}
            onClick={() => void doSectionExport('excel')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12 }}
          >
            <FileSpreadsheet size={13} />
            <span className="section-btn-label">Excel</span>
          </button>
          <button
            className="btn btn-auto btn-sm btn-ghost"
            disabled={!hasRows || !!secBusy}
            title={`Download ${section.title} as PDF`}
            onClick={() => void doSectionExport('pdf')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12 }}
          >
            <FileText size={13} />
            <span className="section-btn-label">PDF</span>
          </button>
          <button
            className="btn btn-auto btn-sm btn-ghost"
            disabled={!hasRows || !!secBusy}
            title={`Share ${section.title}`}
            onClick={() => void doSectionShare()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12 }}
          >
            <Share2 size={13} />
            <span className="section-btn-label">Share</span>
          </button>
        </div>

        <ChevronDown size={16} className="accordion-chevron" />
      </summary>

      <div className="accordion-body">
        {!section.rows.length ? (
          <EmptyState icon="briefcase" title="Nothing recorded here yet" />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {section.columns.map((c) => (
                      <th key={c.key}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((r, i) => {
                    const matId = (r._materialId as string | undefined) || undefined;
                    const matName = (r.material as string | undefined) || undefined;
                    const isMaterialRow = !!(matId || matName);

                    return (
                      <tr
                        key={i}
                        className={isMaterialRow ? 'clickable' : undefined}
                        onClick={() => {
                          if (isMaterialRow && onSelectMaterial) {
                            onSelectMaterial(matId, matName);
                          }
                        }}
                      >
                        {section.columns.map((c) => (
                          <td
                            key={c.key}
                            className={c.numeric ? 'num' : undefined}
                            style={c.key === 'material' ? { color: 'var(--brand)', fontWeight: 600 } : undefined}
                          >
                            {String(r[c.key] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Table Footer Actions placed at the end of each table */}
            <div className="table-footer-actions mobile-only">
              <button
                className="btn btn-auto btn-sm table-footer-btn"
                disabled={!hasRows || !!secBusy}
                onClick={() => void doSectionExport('excel')}
              >
                <FileSpreadsheet size={13} />
                <span>Export Excel</span>
              </button>
              <button
                className="btn btn-auto btn-sm table-footer-btn"
                disabled={!hasRows || !!secBusy}
                onClick={() => void doSectionExport('pdf')}
              >
                <FileText size={13} />
                <span>Export PDF</span>
              </button>
              <button
                className="btn btn-auto btn-sm table-footer-btn"
                disabled={!hasRows || !!secBusy}
                onClick={() => void doSectionShare()}
              >
                <Share2 size={13} />
                <span>Share</span>
              </button>
            </div>
          </>
        )}
      </div>
    </details>
  );
}
